import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { keys, useInvalidate, useJob } from "../api/hooks";
import { adminDirectoryResponseSchema, adminUsersResponseSchema, enqueuedResponseSchema, ownerLinkResponseSchema, type AdminUser, type DirectoryLocation } from "../../api/contracts";
import { formatInstant } from "../lib/format";
import { Badge, Button, ErrorNote, Field, Input, PageHeader, Panel, Select, Spinner } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { Timestamp } from "../components/Timestamp";
import { z } from "zod";

export function DirectoryPage() {
  const invalidate = useInvalidate();
  const directory = useQuery({ queryKey: keys.admin.directory, queryFn: () => api("/api/admin/directory", adminDirectoryResponseSchema) });
  const users = useQuery({ queryKey: keys.admin.users, queryFn: () => api("/api/admin/users", adminUsersResponseSchema) });
  const [q, setQ] = useState("");
  const [syncJob, setSyncJob] = useState<string | null>(null);
  const job = useJob(syncJob);
  const sync = useMutation({
    mutationFn: () => api("/api/admin/jobs", enqueuedResponseSchema, { method: "POST", body: { type: "directory_sync" } }),
    onSuccess: (r) => setSyncJob(r.jobId),
  });
  const link = useMutation({
    mutationFn: (body: { userId: string; ownerIdentifier: string; kind: "manual" | "revoked" }) => api("/api/admin/owner-links", ownerLinkResponseSchema, { method: "POST", body }),
    onSuccess: () => void invalidate(keys.admin.users, keys.admin.directory),
  });
  const unlink = useMutation({
    mutationFn: (id: string) => api(`/api/admin/owner-links/${id}`, z.undefined(), { method: "DELETE" }),
    onSuccess: () => void invalidate(keys.admin.users, keys.admin.directory),
  });
  const [form, setForm] = useState({ userId: "", ownerIdentifier: "" });
  const owners = useMemo(() => [...new Set((directory.data?.locations ?? []).map((l) => l.ownerIdentifier))].sort(), [directory.data]);
  const rows = (directory.data?.locations ?? []).filter((l) => !q || [l.name, l.ownerIdentifier, l.contactEmailNormalized ?? "", l.state ?? ""].join(" ").toLowerCase().includes(q.toLowerCase()));

  useEffect(() => {
    if (job.data?.status === "done" && syncJob && !directory.isFetching && directory.dataUpdatedAt < Date.parse(job.data.finishedAt ?? "0")) void invalidate(keys.admin.directory);
  }, [job.data?.status, job.data?.finishedAt, syncJob, directory.isFetching, directory.dataUpdatedAt]);

  const userColumns: Column<AdminUser>[] = [
    { key: "who", header: "Sign-in", cell: (u) => <div><span className="font-medium">{u.email}</span><p className="text-xs text-hs-text-muted">{u.lastLoginAt ? `last sign-in ${formatInstant(u.lastLoginAt)}` : "never signed in"}</p></div> },
    { key: "groups", header: "Owner groups", cell: (u) => (
      <div className="flex flex-wrap items-center gap-2">
        {u.links.length === 0 && <Badge tone="warning">no owner groups</Badge>}
        {u.links.map((l) => (
          <span key={l.id} className="inline-flex items-center gap-1">
            <Badge tone={l.kind === "revoked" ? "danger" : l.kind === "manual" ? "primary" : "neutral"}>{l.ownerIdentifier} · {l.kind}</Badge>
            {l.kind !== "revoked" && <Button variant="ghost" size="sm" onClick={() => link.mutate({ userId: u.id, ownerIdentifier: l.ownerIdentifier, kind: "revoked" })}>Revoke</Button>}
            <Button variant="ghost" size="sm" onClick={() => unlink.mutate(l.id)}>Remove</Button>
          </span>
        ))}
      </div>
    ) },
  ];
  const locationColumns: Column<DirectoryLocation>[] = [
    { key: "name", header: "Location", cell: (l) => l.name },
    { key: "owner", header: "Owner group", cell: (l) => l.ownerIdentifier },
    { key: "state", header: "State", width: "5rem", cell: (l) => l.state ?? "—" },
    { key: "email", header: "Contact email", cell: (l) => l.contactEmailNormalized ?? <span className="text-hs-text-subtle">none</span> },
    { key: "linked", header: "Linked sign-ins", cell: (l) => (l.linkedUsers.length ? l.linkedUsers.join(", ") : <Badge tone="warning">nobody</Badge>) },
    { key: "synced", header: "Synced", width: "9rem", cell: (l) => <Timestamp at={l.syncedAt} /> },
  ];

  return (
    <>
      <PageHeader title="Owner directory" subtitle="Locations, their owner group and contact email from the data warehouse, and which sign-ins are linked to each owner group."
        actions={<Button onClick={() => sync.mutate()} disabled={sync.isPending || (job.data ? job.data.status === "queued" || job.data.status === "running" : false)}>Sync now</Button>} />
      <ErrorNote error={sync.error ?? link.error ?? unlink.error ?? directory.error ?? users.error} />
      {job.data && <p className="mb-3 text-sm text-hs-text-muted">Sync {job.data.status}{job.data.lastError ? `: ${job.data.lastError}` : ""}{job.data.status === "done" ? ` · ${JSON.stringify(job.data.result)}` : ""}</p>}

      <Panel className="mb-4" title="Sign-ins and their owner groups" padding="none"
        footer={
          <form className="flex w-full flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); if (form.userId && form.ownerIdentifier) link.mutate({ ...form, kind: "manual" }); }}>
            <Field label="Sign-in" className="w-64"><Select value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })}><option value="">Choose</option>{(users.data?.users ?? []).map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}</Select></Field>
            <Field label="Owner group" className="w-64"><Input list="owner-groups" placeholder="Exact owner identifier from the directory" value={form.ownerIdentifier} onChange={(e) => setForm({ ...form, ownerIdentifier: e.target.value })} /><datalist id="owner-groups">{owners.map((o) => <option key={o} value={o} />)}</datalist></Field>
            <Button type="submit" disabled={link.isPending}>Link manually</Button>
          </form>
        }>
        {users.isPending ? <div className="p-4"><Spinner /></div> : <DataTable rows={users.data?.users ?? []} rowKey={(u) => u.id} density="compact" caption="Sign-ins" columns={userColumns} />}
      </Panel>

      <Panel padding="none"
        actions={<Input className="w-72" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, owner, email, state" aria-label="Search directory" />}
        footer={<p className="text-xs text-hs-text-muted tabular-nums">{rows.length} of {directory.data?.locations.length ?? 0} locations</p>}>
        {directory.isPending ? <div className="p-4"><Spinner /></div> : <DataTable rows={rows} rowKey={(l) => l.blvdId} density="compact" stickyHeader caption="Owner directory" columns={locationColumns} />}
      </Panel>
    </>
  );
}
